import { OPS_AUTH, USE_MOCK_AUTH } from "../config/api";
import { OPS_PERMISSION_GROUPS } from "../config/permissions";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";

/** Prefer static FE labels when the API key matches. */
const STATIC_LABEL_BY_KEY = Object.fromEntries(
  OPS_PERMISSION_GROUPS.flatMap((group) =>
    group.permissions.map((item) => [item.key, item.label])
  )
);

const MODULE_LABELS = {
  dashboard: "Dashboard",
  institutions: "Institutions",
  assignment: "Case assignment",
  cases: "Cases",
  customers: "Customers",
  contact_centre: "Contact centre",
  communications: "Communications",
  payments: "Payments",
  settlements: "Settlements",
  escalations: "Escalations",
  legal: "Legal",
  settings: "Settings",
  portfolios: "Portfolios",
  segments: "Segments",
  reports: "Reports",
  ops_users: "Team",
};

function humanizePermissionName(name) {
  if (STATIC_LABEL_BY_KEY[name]) return STATIC_LABEL_BY_KEY[name];
  const [module, action] = String(name).split(".");
  if (!action) return String(name).replace(/_/g, " ");
  const actionLabel = action.replace(/_/g, " ");
  const moduleLabel = (module || "").replace(/_/g, " ");
  return `${actionLabel.charAt(0).toUpperCase()}${actionLabel.slice(1)} ${moduleLabel}`;
}

function moduleLabel(moduleKey) {
  return (
    MODULE_LABELS[moduleKey] ||
    String(moduleKey)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function permissionEntry(item, moduleKey) {
  if (!item || typeof item !== "object") return null;
  const key = item.name || item.key || item.permission_slug;
  if (!key) return null;
  return {
    key,
    label: item.label || humanizePermissionName(key),
    permission_slug: item.permission_slug || item.name || key,
    module: item.module || moduleKey,
  };
}

/**
 * The permissions endpoint returns `{ groups: [{ module, permissions }] }`.
 * Older payloads used a module → permission[] map. Both become UI groups.
 */
export function normalizeOpsPermissionCatalog(data) {
  if (!data || typeof data !== "object") {
    return { groups: OPS_PERMISSION_GROUPS, byKey: {}, bySlug: {} };
  }

  const sourceGroups = Array.isArray(data.groups)
    ? data.groups.map((group) => ({
        module: group.module || group.label,
        label: group.label,
        permissions: Array.isArray(group.permissions) ? group.permissions : [],
      }))
    : Object.entries(data)
        .filter(([, items]) => Array.isArray(items))
        .map(([moduleKey, items]) => ({
          module: moduleKey,
          label: null,
          permissions: items.filter((item) => item && typeof item === "object"),
        }));

  const byKey = {};
  const bySlug = {};
  const groups = sourceGroups
    .map((group) => {
      const moduleKey = group.module || "general";
      const permissions = group.permissions
        .map((item) => permissionEntry(item, moduleKey))
        .filter(Boolean);
      permissions.forEach((entry) => {
        byKey[entry.key] = entry;
        if (entry.permission_slug) bySlug[entry.permission_slug] = entry;
      });
      return {
        label: group.label || moduleLabel(moduleKey),
        module: moduleKey,
        permissions,
      };
    })
    .filter((group) => group.permissions.length > 0);

  if (!groups.length) {
    return { groups: OPS_PERMISSION_GROUPS, byKey: {}, bySlug: {} };
  }

  return { groups, byKey, bySlug };
}

/**
 * GET /api/ops/permissions — catalog for role permission assignment.
 * Dedupes concurrent/StrictMode remounts so only one network call runs per token.
 * @param {string} token
 */
let permissionsCache = {
  token: null,
  promise: null,
  result: null,
};

export async function fetchOpsPermissions(token) {
  if (USE_MOCK_AUTH || !token) {
    return normalizeOpsPermissionCatalog(null);
  }

  if (permissionsCache.token === token && permissionsCache.result) {
    return permissionsCache.result;
  }

  if (permissionsCache.token === token && permissionsCache.promise) {
    return permissionsCache.promise;
  }

  permissionsCache = {
    token,
    result: null,
    promise: (async () => {
      const json = await apiRequest(OPS_AUTH.permissions, {
        method: "GET",
        token,
      });
      const data = unwrapApiEnvelope(json);
      const catalog = normalizeOpsPermissionCatalog(data);
      permissionsCache = { token, promise: null, result: catalog };
      return catalog;
    })().catch((err) => {
      permissionsCache = { token: null, promise: null, result: null };
      throw err;
    }),
  };

  return permissionsCache.promise;
}

/** Clear cached catalog (e.g. after logout). */
export function clearOpsPermissionsCache() {
  permissionsCache = { token: null, promise: null, result: null };
}
